import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IdCard, CheckCircle2, Clock, AlertTriangle, UserRound } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";

function statusMeta(m) {
  switch (m) {
    case "active": return { label: "Active", icon: CheckCircle2, className: "bg-emerald-500 hover:bg-emerald-500 text-white" };
    case "pending_review": return { label: "Pending Review", icon: Clock, className: "bg-amber-500 hover:bg-amber-500 text-white" };
    case "pending_payment": return { label: "Pending Payment", icon: Clock, className: "bg-amber-500 hover:bg-amber-500 text-white" };
    case "rejected": return { label: "Rejected", icon: AlertTriangle, className: "bg-rose-500 hover:bg-rose-500 text-white" };
    case "suspended": return { label: "Suspended", icon: AlertTriangle, className: "bg-slate-400 hover:bg-slate-400 text-white" };
    default: return { label: m || "Not Started", icon: UserRound, className: "bg-muted text-foreground" };
  }
}

/**
 * MemberOverviewCard — shown in the Admin Dashboard header so an office bearer
 * (state or district admin) can see their OWN membership status at a glance.
 */
export default function MemberOverviewCard({ user }) {
  if (!user) return null;
  const paid = user.payment_status === "paid";
  const meta = statusMeta(user.membership_status);
  const Icon = meta.icon;
  return (
    <Card className="p-4 md:p-5 border-brand-blue/30 bg-gradient-to-br from-brand-blue/5 to-transparent" data-testid="admin-member-overview">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
          <IdCard className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-brand-blue font-semibold">Your Membership</div>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <h3 className="text-base font-bold truncate">{user.full_name || user.email}</h3>
            <span className="text-xs text-muted-foreground">· {ROLE_LABELS[user.role] || user.role}</span>
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge className={meta.className} data-testid="overview-membership-badge">
              <Icon className="w-3 h-3 mr-1" /> {meta.label}
            </Badge>
            <Badge className={paid ? "bg-emerald-500 hover:bg-emerald-500 text-white" : "bg-amber-500 hover:bg-amber-500 text-white"} data-testid="overview-payment-badge">
              {paid ? "Paid" : "Unpaid"}
            </Badge>
            {user.membership_id && (
              <span className="text-xs font-mono text-muted-foreground" data-testid="overview-membership-id">{user.membership_id}</span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="h-8" data-testid="overview-open-member-dashboard">
                Open Member Dashboard
              </Button>
            </Link>
            {!user.membership_id && (
              <Link to="/register">
                <Button size="sm" className="h-8 bg-brand-blue hover:bg-brand-blue/90 text-white" data-testid="overview-complete-membership">
                  Complete Membership Form
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
